import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { getPendingGiftCards, approveGiftCard, rejectGiftCard } from '../../firebase/giftcards';
import { formatPrice } from '../../utils/formatPrice';

const REJECTION_REASONS = [
  "Foto onduidelijk of onleesbaar",
  "Saldo komt niet overeen",
  "Cadeaubon verlopen of bijna verlopen",
  "Dubbele listing gedetecteerd",
  "Vermoeden van fraude",
  "Andere reden (zie notities)"
];

const STATS_PERIODS = [
  { id: 'today', label: 'Vandaag' },
  { id: 'week', label: 'Deze week' },
  { id: 'month', label: 'Deze maand' },
  { id: 'quarter', label: 'Dit kwartaal' },
  { id: 'all', label: 'Alle tijd' },
];

const LEGACY_PENDING_STATUS = 'pending_verification';
const LEGACY_ACTIVE_STATUS = 'approved';
const CURRENT_PENDING_STATUS = 'pendingAdminReview';
const CURRENT_ACTIVE_STATUS = 'active';

const integerFormatter = new Intl.NumberFormat('nl-NL');
const decimalFormatter = new Intl.NumberFormat('nl-NL', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const normalizeListingStatus = (status) => {
  if (status === LEGACY_PENDING_STATUS) return CURRENT_PENDING_STATUS;
  if (status === LEGACY_ACTIVE_STATUS) return CURRENT_ACTIVE_STATUS;
  return status;
};

const formatCount = (value) => integerFormatter.format(Number(value || 0));

const formatMetricNumber = (value) => decimalFormatter.format(Number(value || 0));

const formatPercentage = (value) => `${formatMetricNumber(value)}%`;

const REFUND_STATUS_META = {
  refunded: {
    copy: 'Terugbetaling verwerkt via OPP.',
    className: 'bg-emerald-100 text-emerald-700',
  },
  refund_pending: {
    copy: 'Terugbetaling in behandeling bij OPP.',
    className: 'bg-amber-100 text-amber-700',
  },
  refund_failed: {
    copy: 'Terugbetaling via OPP mislukt - handmatige actie nodig.',
    className: 'bg-red-100 text-red-700',
  },
  manual_required: {
    copy: 'Geen OPP-transactie gekoppeld - handmatige terugbetaling nodig.',
    className: 'bg-red-100 text-red-700',
  },
};

const getRefundStatusMeta = (status) => {
  if (!status) return null;
  return REFUND_STATUS_META[status] || {
    copy: status,
    className: 'bg-slate-100 text-slate-700',
  };
};

const RefundStatusBadge = ({ status }) => {
  const meta = getRefundStatusMeta(status);
  if (!meta) return <span className="text-sm text-slate-400">-</span>;

  return (
    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${meta.className}`}>
      {meta.copy}
    </span>
  );
};

const getStartOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const getRangeStart = (period) => {
  const now = new Date();
  const start = new Date(now);

  switch (period) {
    case 'today':
      return getStartOfDay(now);
    case 'week': {
      const dayIndex = (now.getDay() + 6) % 7;
      start.setDate(now.getDate() - dayIndex);
      return getStartOfDay(start);
    }
    case 'month':
      return new Date(now.getFullYear(), now.getMonth(), 1);
    case 'quarter':
      return new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
    case 'all':
    default:
      return null;
  }
};

const StatsMetricCard = ({ label, value, helper }) => (
  <div className="rounded-3xl border border-orange-100 bg-white p-5 shadow-[0_20px_60px_-40px_rgba(234,88,12,0.35)]">
    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-red-500">{label}</p>
    <p className="mt-3 text-2xl font-bold text-slate-900">{value}</p>
    {helper ? <p className="mt-2 text-sm text-slate-500">{helper}</p> : null}
  </div>
);

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [pendingCards, setPendingCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [activeTab, setActiveTab] = useState('queue');
  const [merchants, setMerchants] = useState([]);
  const [merchantsLoading, setMerchantsLoading] = useState(false);
  const [pendingSales, setPendingSales] = useState([]);
  const [pendingSalesLoading, setPendingSalesLoading] = useState(false);
  const [escrowQueue, setEscrowQueue] = useState([]);
  const [escrowQueueLoading, setEscrowQueueLoading] = useState(false);
  const [verifiedBalances, setVerifiedBalances] = useState({});
  const [selectedSale, setSelectedSale] = useState(null);
  const [showSaleRejectModal, setShowSaleRejectModal] = useState(false);
  const [saleRejectionReason, setSaleRejectionReason] = useState('');
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsPeriod, setStatsPeriod] = useState('all');
  const [statsCards, setStatsCards] = useState([]);
  const [statsTransactions, setStatsTransactions] = useState([]);
  const [statsUsers, setStatsUsers] = useState([]);
  const [selectedStatsTransactionId, setSelectedStatsTransactionId] = useState(null);
  
  const navigate = useNavigate();
  const auth = getAuth();
 

  // Check if user is admin
useEffect(() => {
  // ✅ FIX: Use onAuthStateChanged instead of auth.currentUser
  const unsubscribe = onAuthStateChanged(auth, async (user) => {
    if (!user) {
      alert('Je moet ingelogd zijn om deze pagina te bekijken.');
      navigate('/login', { state: { from: { pathname: '/admin' } } });
      return;
    }

    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));

      if (userDoc.exists() && userDoc.data().role === 'admin') {
        setIsAdmin(true);
        await user.getIdToken(true); // Force refresh token
        loadPendingCards();
        loadMerchants();
      } else {
        alert('Geen toegang. Alleen admins kunnen deze pagina bekijken.');
        navigate('/');
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
      alert('Fout bij controleren van admin rechten.');
      navigate('/');
    } finally {
      setLoading(false);
    }
  });

  return () => unsubscribe();
}, [auth, navigate]);

  const loadPendingCards = async () => {
    try {
      
      setLoading(true);
      const cards = await getPendingGiftCards();

       // Fetch private details for each card (admin can read these)
    const cardsWithPrivateData = await Promise.all(
      cards.map(async (card) => {
        try {
          // Get seller info
          const sellerInfoDoc = await getDoc(doc(db, `giftcards/${card.id}/private/seller_info`));
          const sellerInfo = sellerInfoDoc.exists() ? sellerInfoDoc.data() : {};
          
          // Get card details
          const cardDetailsDoc = await getDoc(doc(db, `giftcards/${card.id}/private/card_details`));
          const cardDetails = cardDetailsDoc.exists() ? cardDetailsDoc.data() : {};
          
          const sellerDoc = await getDoc(doc(db, 'users', card.sellerId));
          const sellerData = sellerDoc.exists() ? sellerDoc.data() : {};

          return {
            ...card,
            ...sellerInfo,
            ...cardDetails,
            sellerCompliance: sellerData.compliance, 
            opp_merchant_uid: sellerData.opp_merchant_uid,
            completedSalesCount: sellerData.completedSalesCount ?? 0,
            cardValue: card.originalValue

          };
        } catch (error) {
          console.error(`Error fetching private data for card ${card.id}:`, error);
          return card; 
        }
      })
    );

      setPendingCards(cardsWithPrivateData);
    } catch (error) {
      console.error('Error loading pending cards:', error);
      alert('Fout bij het laden van cadeaubonnen');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (card) => {
    const verifiedBalance = Number(verifiedBalances[card.id]);
    if (!Number.isFinite(verifiedBalance) || verifiedBalance <= 0) {
      alert('Voer eerst het geverifieerde saldo in.');
      return;
    }

    if (!window.confirm(`Cadeaubon ${card.brand} €${card.cardValue} goedkeuren?`)) return;

    setActionLoading(true);
    try {
      await approveGiftCard(card.id, verifiedBalance);
      
      alert('Cadeaubon goedgekeurd!');
      await loadPendingCards();
    } catch (error) {
      console.error('Error approving card:', error);
      alert('Fout bij goedkeuren: ' + error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const loadPendingSales = async () => {
    setPendingSalesLoading(true);
    try {
      const soldCardsQuery = query(
        collection(db, 'giftcards'),
        where('status', '==', 'sold')
      );
      const soldCardsSnapshot = await getDocs(soldCardsQuery);

      const cards = await Promise.all(
        soldCardsSnapshot.docs.map(async (cardDoc) => {
          const card = { id: cardDoc.id, ...cardDoc.data() };

          try {
            const [transactionDoc, sellerDoc] = await Promise.all([
              getDoc(doc(db, `giftcards/${card.id}/private/transaction`)),
              getDoc(doc(db, 'users', card.sellerId))
            ]);

            if (!transactionDoc.exists()) {
              return null;
            }

            const transactionData = transactionDoc.data();
            if (transactionData.status !== 'pendingAdminApproval') {
              return null;
            }

            const sellerData = sellerDoc.exists() ? sellerDoc.data() : {};

            return {
              ...card,
              ...transactionData,
              sellerEmail: sellerData.email || transactionData.sellerEmail || 'Onbekend',
              sellerName: sellerData.displayName || sellerData.name || transactionData.sellerName || 'Onbekend',
              completedSalesCount: sellerData.completedSalesCount ?? 0
            };
          } catch (error) {
            console.error(`Error loading pending sale for card ${card.id}:`, error);
            return null;
          }
        })
      );

      setPendingSales(cards.filter(Boolean));
    } catch (error) {
      console.error('Error loading pending sales:', error);
      alert('Fout bij het laden van pending sales');
    } finally {
      setPendingSalesLoading(false);
    }
  };

  const loadMerchants = async () => {
    setMerchantsLoading(true);
    try {
      const functionsInstance = getFunctions(undefined, 'europe-west1');
      const getAdminMerchants = httpsCallable(functionsInstance, 'getAdminMerchants');
      const result = await getAdminMerchants();
      setMerchants(result.data.merchants);
    } catch (error) {
      console.error('Error loading merchants:', error);
    } finally {
      setMerchantsLoading(false);
    }
  };

  const loadEscrowQueue = async () => {
    setEscrowQueueLoading(true);
    try {
      const functionsInstance = getFunctions(undefined, 'europe-west1');
      const getOppTransactionStatus = httpsCallable(functionsInstance, 'getOppTransactionStatus');
      const soldCardsQuery = query(
        collection(db, 'giftcards'),
        where('status', '==', 'sold')
      );
      const soldCardsSnapshot = await getDocs(soldCardsQuery);

      const cards = await Promise.all(
        soldCardsSnapshot.docs.map(async (cardDoc) => {
          const card = { id: cardDoc.id, ...cardDoc.data() };

          try {
            const [transactionDoc, sellerDoc] = await Promise.all([
              getDoc(doc(db, `giftcards/${card.id}/private/transaction`)),
              getDoc(doc(db, 'users', card.sellerId))
            ]);

            if (!transactionDoc.exists()) {
              return null;
            }

            const transactionData = transactionDoc.data();
            if (transactionData.status !== 'sold') {
              return null;
            }

            const sellerData = sellerDoc.exists() ? sellerDoc.data() : {};
            let oppStatus = null;
            if (card.oppTransactionUid || transactionData.oppTransactionUid) {
              try {
                const statusResult = await getOppTransactionStatus({
                  cardId: card.id,
                  oppTransactionUid: card.oppTransactionUid || transactionData.oppTransactionUid,
                });
                oppStatus = statusResult.data;
              } catch (statusError) {
                console.error(`Error loading OPP status for card ${card.id}:`, statusError);
              }
            }

            return {
              ...card,
              ...transactionData,
              sellerEmail: sellerData.email || transactionData.sellerEmail || 'Onbekend',
              sellerName: sellerData.displayName || sellerData.name || transactionData.sellerName || 'Onbekend',
              completedSalesCount: sellerData.completedSalesCount ?? 0,
              oppStatus: oppStatus?.status || null,
              oppCanExtendEscrow: Boolean(oppStatus?.canExtendEscrow),
              oppEscrow: oppStatus?.escrow || null,
            };
          } catch (error) {
            console.error(`Error loading escrow queue for card ${card.id}:`, error);
            return null;
          }
        })
      );

      const sortedCards = cards
        .filter(Boolean)
        .sort((a, b) => getTimestampMs(a.escrowEligibleAt) - getTimestampMs(b.escrowEligibleAt));

      setEscrowQueue(sortedCards);
    } catch (error) {
      console.error('Error loading escrow queue:', error);
      alert('Fout bij het laden van escrow transacties');
    } finally {
      setEscrowQueueLoading(false);
    }
  };

  const loadStats = async () => {
    setStatsLoading(true);
    try {
      const [giftcardsSnapshot, usersSnapshot] = await Promise.all([
        getDocs(collection(db, 'giftcards')),
        getDocs(collection(db, 'users')),
      ]);

      const usersById = new Map();
      const users = usersSnapshot.docs.map((userDoc) => {
        const user = { id: userDoc.id, ...userDoc.data() };
        usersById.set(userDoc.id, user);
        return user;
      });

      const cards = giftcardsSnapshot.docs.map((cardDoc) => ({
        id: cardDoc.id,
        ...cardDoc.data(),
        status: normalizeListingStatus(cardDoc.data().status),
      }));

      const transactions = (
        await Promise.all(
          cards.map(async (card) => {
            try {
              const transactionDoc = await getDoc(doc(db, `giftcards/${card.id}/private/transaction`));

              if (!transactionDoc.exists()) {
                return null;
              }

              const transactionData = transactionDoc.data();
              const sellerData = usersById.get(card.sellerId) || {};
              const buyerData = transactionData.buyerId ? usersById.get(transactionData.buyerId) || {} : {};

              return {
                id: card.id,
                cardId: card.id,
                brand: card.brand || transactionData.brand || 'Onbekend',
                originalValue: Number(card.originalValue ?? transactionData.originalValue ?? 0),
                sellingPrice: Number(card.sellingPrice ?? transactionData.amount ?? transactionData.sellingPrice ?? 0),
                platformFee: Number(card.platformFee ?? transactionData.platformFee ?? 0),
                sellerPayout: Number(card.sellerPayout ?? transactionData.sellerPayout ?? 0),
                buyerEmail: transactionData.buyerEmail || buyerData.email || card.buyerEmail || 'Onbekend',
                sellerEmail: sellerData.email || transactionData.sellerEmail || card.sellerEmail || 'Onbekend',
                sellerId: card.sellerId || transactionData.sellerId || null,
                buyerId: transactionData.buyerId || card.buyerId || null,
                paymentId: transactionData.paymentId || card.paymentId || null,
                status: transactionData.status || 'unknown',
                refundStatus: transactionData.refundStatus || card.refundStatus || null,
                disputeStatus: transactionData.disputeStatus || 'none',
                createdAt: transactionData.createdAt || card.createdAt || null,
                completedAt: transactionData.completedAt || null,
                escrowEligibleAt: transactionData.escrowEligibleAt || null,
                escrowReleasedAt: transactionData.escrowReleasedAt || null,
                completedSalesCount: sellerData.completedSalesCount ?? 0,
                sellerCreatedAt: sellerData.authCreatedAt || sellerData.createdAt || null,
              };
            } catch (error) {
              console.error(`Error loading stats transaction for card ${card.id}:`, error);
              return null;
            }
          })
        )
      ).filter(Boolean);

      setStatsCards(cards);
      setStatsTransactions(transactions);
      setStatsUsers(users);
      setSelectedStatsTransactionId((current) => (
        current && transactions.some((transaction) => transaction.id === current)
          ? current
          : transactions[0]?.id || null
      ));
    } catch (error) {
      console.error('Error loading stats:', error);
      alert('Fout bij het laden van statistieken');
    } finally {
      setStatsLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason) {
      alert('Selecteer een reden voor afwijzing');
      return;
    }

    setActionLoading(true);
    try {
      await rejectGiftCard(selectedCard.id, rejectionReason);
      
      alert('Cadeaubon afgewezen');
      setShowRejectModal(false);
      setSelectedCard(null);
      setRejectionReason('');
      await loadPendingCards();
    } catch (error) {
      console.error('Error rejecting card:', error);
      alert('Fout bij afwijzen: ' + error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleApproveSale = async (sale) => {
    const verifiedBalance = Number(verifiedBalances[sale.id]);
    if (!Number.isFinite(verifiedBalance) || verifiedBalance <= 0) {
      alert('Voer eerst het geverifieerde saldo in.');
      return;
    }

    if (!window.confirm(`Verkoop van ${sale.brand} goedkeuren?`)) return;

    setActionLoading(true);
    try {
      const functionsInstance = getFunctions(undefined, 'europe-west1');
      const approveSale = httpsCallable(functionsInstance, 'approveSale');
      await approveSale({ cardId: sale.id, verifiedBalance });

      alert('Verkoop goedgekeurd.');
      await Promise.all([loadPendingSales(), loadEscrowQueue()]);
    } catch (error) {
      console.error('Error approving sale:', error);
      alert('Fout bij goedkeuren van sale: ' + (error.message || 'Onbekende fout'));
    } finally {
      setActionLoading(false);
    }
  };

  const openSaleRejectModal = (sale) => {
    setSelectedSale(sale);
    setShowSaleRejectModal(true);
  };

  const handleRejectSale = async () => {
    if (!saleRejectionReason) {
      alert('Voer een reden voor afwijzing in.');
      return;
    }

    setActionLoading(true);
    try {
      const functionsInstance = getFunctions(undefined, 'europe-west1');
      const rejectSale = httpsCallable(functionsInstance, 'rejectSale');
      const result = await rejectSale({ cardId: selectedSale.id, reason: saleRejectionReason });
      const refundStatus = result.data?.refundStatus;
      const refundMessages = {
        refunded: 'Verkoop afgewezen. Terugbetaling verwerkt via OPP.',
        refund_pending: 'Verkoop afgewezen. Terugbetaling in behandeling bij OPP.',
        refund_failed: 'Verkoop afgewezen. Terugbetaling via OPP mislukt - handmatige actie nodig.',
        manual_required: 'Verkoop afgewezen. Geen OPP-transactie gekoppeld - handmatige terugbetaling nodig.',
      };

      alert(refundMessages[refundStatus] || 'Verkoop afgewezen. Controleer de terugbetalingstatus.');
      setShowSaleRejectModal(false);
      setSelectedSale(null);
      setSaleRejectionReason('');
      await Promise.all([loadPendingSales(), loadPendingCards()]);
    } catch (error) {
      console.error('Error rejecting sale:', error);
      alert('Fout bij afwijzen van sale: ' + (error.message || 'Onbekende fout'));
    } finally {
      setActionLoading(false);
    }
  };

  const openRejectModal = (card) => {
    setSelectedCard(card);
    setShowRejectModal(true);
  };

  const handleMarkTransactionCompleted = async (card) => {
    if (!window.confirm(`OPP status synchroniseren voor ${card.brand} (${card.id})?`)) return;

    setActionLoading(true);
    try {
      const functionsInstance = getFunctions(undefined, 'europe-west1');
      const markTransactionCompleted = httpsCallable(functionsInstance, 'markTransactionCompleted');
      const result = await markTransactionCompleted({ cardId: card.id });

      alert(result.data?.message || 'OPP status gesynchroniseerd.');
      await loadEscrowQueue();

      if (merchants.length > 0) {
        await loadMerchants();
      }
    } catch (error) {
      console.error('Error syncing transaction status:', error);
      alert('Fout bij synchroniseren van OPP status: ' + (error.message || 'Onbekende fout'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleExtendEscrow = async (card) => {
    if (!window.confirm(`Escrow met 48 uur verlengen voor ${card.brand} (${card.id})?`)) return;

    setActionLoading(true);
    try {
      const functionsInstance = getFunctions(undefined, 'europe-west1');
      const extendEscrow = httpsCallable(functionsInstance, 'extendEscrow');
      const result = await extendEscrow({ cardId: card.id, extensionHours: 48 });
      alert(`Escrow verlengd tot ${new Date(result.data.escrowDate).toLocaleString('nl-NL')}.`);
      await loadEscrowQueue();
    } catch (error) {
      console.error('Error extending escrow:', error);
      alert('Fout bij verlengen van escrow: ' + (error.message || 'Onbekende fout'));
    } finally {
      setActionLoading(false);
    }
  };

  const getBalanceCheckUrl = (brand, cardNumber) => {
  const urls = {
    'Bol.com': `https://www.bol.com/nl/nl/sf/cadeaubon-bestellen/`,
    'Coolblue': `https://www.coolblue.nl/saldo-checker`,
    'de Bijenkorf': `https://www.debijenkorf.nl/klantenservice/De-Bijenkorf-Cadeaucard`,
    'Douglas': 'https://www.douglas.nl/nl/cp/gift-card-details-page/giftcarddetails#Saldochecker'
  };
  return urls[brand];
};

  const getSellerMaturity = (sellerId, completedSalesCount = 0) => {
    const merchant = merchants.find((item) => item.uid === sellerId);
    if (merchant) {
      return merchant.isNewSeller ? 'Nieuw' : 'Gevestigd';
    }

    return completedSalesCount < 5 ? 'Nieuw' : 'Gevestigd';
  };





  const formatDate = (timestamp) => {
    if (!timestamp) return 'Onbekend';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('nl-NL', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimestampMs = (timestamp) => {
    if (!timestamp) return 0;
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return Number.isNaN(date.getTime()) ? 0 : date.getTime();
  };

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const seconds = Math.floor((new Date() - date) / 1000);
    
    if (seconds < 60) return `${seconds}s geleden`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m geleden`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}u geleden`;
    return `${Math.floor(seconds / 86400)}d geleden`;
  };

  const getTimeUntil = (timestamp) => {
    if (!timestamp) return 'Onbekend';
    const diffMs = getTimestampMs(timestamp) - Date.now();

    if (diffMs <= 0) return 'Nu vrij te geven';

    const totalMinutes = Math.ceil(diffMs / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours === 0) return `${minutes}m resterend`;
    return `${hours}u ${minutes}m resterend`;
  };

  const isEscrowEligible = (timestamp) => getTimestampMs(timestamp) <= Date.now();

  const selectedStatsStart = getRangeStart(statsPeriod);

  const isWithinStatsRange = (timestamp) => {
    if (!selectedStatsStart) return true;
    const timestampMs = getTimestampMs(timestamp);
    if (!timestampMs) return false;
    return timestampMs >= selectedStatsStart.getTime();
  };

  const filteredStatsCards = statsCards.filter((card) => isWithinStatsRange(card.createdAt));

  const filteredStatsTransactions = statsTransactions
    .filter((transaction) => isWithinStatsRange(transaction.createdAt))
    .sort((a, b) => getTimestampMs(b.createdAt) - getTimestampMs(a.createdAt));

  const selectedStatsTransaction = filteredStatsTransactions.find(
    (transaction) => transaction.id === selectedStatsTransactionId
  ) || filteredStatsTransactions[0] || null;

  const completedTransactions = filteredStatsTransactions.filter((transaction) => transaction.status === 'completed');
  const rejectedTransactions = filteredStatsTransactions.filter((transaction) => transaction.status === 'rejected');
  const pendingTransactions = filteredStatsTransactions.filter(
    (transaction) => transaction.status !== 'completed' && transaction.status !== 'rejected'
  );

  const totalGMV = completedTransactions.reduce((sum, transaction) => sum + Number(transaction.sellingPrice || 0), 0);
  const totalRevenue = completedTransactions.reduce((sum, transaction) => sum + Number(transaction.platformFee || 0), 0);
  const totalListed = filteredStatsCards.length;
  const totalActiveCards = filteredStatsCards.filter((card) => normalizeListingStatus(card.status) === 'active').length;
  const totalSoldCards = filteredStatsCards.filter((card) => normalizeListingStatus(card.status) === 'sold').length;
  const averageCardFaceValue = totalListed
    ? filteredStatsCards.reduce((sum, card) => sum + Number(card.originalValue || 0), 0) / totalListed
    : 0;
  const averageSellingPrice = totalListed
    ? filteredStatsCards.reduce((sum, card) => sum + Number(card.sellingPrice || 0), 0) / totalListed
    : 0;
  const averageDiscountPercentage = totalListed
    ? filteredStatsCards.reduce((sum, card) => {
        const originalValue = Number(card.originalValue || 0);
        const sellingPrice = Number(card.sellingPrice || 0);

        if (!originalValue) return sum;
        return sum + (((originalValue - sellingPrice) / originalValue) * 100);
      }, 0) / totalListed
    : 0;
  const averagePlatformFee = filteredStatsTransactions.length
    ? filteredStatsTransactions.reduce((sum, transaction) => sum + Number(transaction.platformFee || 0), 0) / filteredStatsTransactions.length
    : 0;
  const disputeCount = completedTransactions.filter((transaction) => {
    const disputeStatus = transaction.disputeStatus || 'none';
    return disputeStatus !== 'none' && disputeStatus !== 'resolvedForSeller';
  }).length;
  const rejectedListings = filteredStatsCards.filter((card) => normalizeListingStatus(card.status) === 'rejected').length;
  const disputeRate = completedTransactions.length ? (disputeCount / completedTransactions.length) * 100 : 0;
  const rejectionRate = totalListed ? (rejectedListings / totalListed) * 100 : 0;
  const sellerIdsInPeriod = new Set(
    filteredStatsCards
      .map((card) => card.sellerId)
      .concat(filteredStatsTransactions.map((transaction) => transaction.sellerId))
      .filter(Boolean)
  );
  const totalSellers = sellerIdsInPeriod.size;
  const totalBuyers = new Set(
    filteredStatsTransactions.map((transaction) => transaction.buyerId || transaction.buyerEmail).filter(Boolean)
  ).size;
  const newSellers = new Set(
    statsUsers
      .filter((user) => sellerIdsInPeriod.has(user.id) && isWithinStatsRange(user.authCreatedAt || user.createdAt))
      .map((user) => user.id)
  ).size;

  const statsMetricCards = [
    { label: 'Transacties voltooid', value: formatCount(completedTransactions.length), helper: 'Succesvol afgerond' },
    { label: 'Transacties open', value: formatCount(pendingTransactions.length), helper: 'Nog in behandeling of escrow' },
    { label: 'Transacties afgewezen', value: formatCount(rejectedTransactions.length), helper: 'Afgekeurd of terugbetaald' },
    { label: 'GMV', value: formatPrice(totalGMV), helper: 'Som van verkochte kaarten' },
    { label: 'Revenue', value: formatPrice(totalRevenue), helper: 'Som van platformkosten' },
    { label: 'Kaarten geplaatst', value: formatCount(totalListed), helper: 'Alle listings in periode' },
    { label: 'Kaarten actief', value: formatCount(totalActiveCards), helper: 'Nu live op de marktplaats' },
    { label: 'Kaarten verkocht', value: formatCount(totalSoldCards), helper: 'Status verkocht' },
    { label: 'Gem. kaartwaarde', value: formatPrice(averageCardFaceValue), helper: 'Originele waarde' },
    { label: 'Gem. verkoopprijs', value: formatPrice(averageSellingPrice), helper: 'Prijs voor koper' },
    { label: 'Gem. korting', value: formatPercentage(averageDiscountPercentage), helper: 'Ten opzichte van kaartwaarde' },
    { label: 'Gem. platformkosten', value: formatPrice(averagePlatformFee), helper: 'Per transactie' },
    { label: 'Dispuutratio', value: formatPercentage(disputeRate), helper: 'Disputen / completed' },
    { label: 'Afwijsratio', value: formatPercentage(rejectionRate), helper: 'Afgewezen listings / totaal' },
    { label: 'Verkopers', value: formatCount(totalSellers), helper: 'Unieke verkopers in periode' },
    { label: 'Kopers', value: formatCount(totalBuyers), helper: 'Unieke kopers in periode' },
    { label: 'Nieuwe verkopers', value: formatCount(newSellers), helper: 'Accounts aangemaakt in periode' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
            </div>
            <button
              onClick={() => (
                activeTab === 'queue' ?
                  loadPendingCards() :
                  activeTab === 'pendingSales' ?
                    loadPendingSales() :
                  activeTab === 'stats' ?
                    loadStats() :
                  activeTab === 'escrow' ?
                    loadEscrowQueue() :
                    loadMerchants()
              )}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
            >
              ↻ Ververs
            </button>
          </div>
          {/* Tabs */}
          <div className="flex space-x-4 border-b border-gray-200 -mb-4">
            <button
              onClick={() => setActiveTab('queue')}
              className={`pb-3 px-1 font-semibold text-sm transition border-b-2 ${
                activeTab === 'queue'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Verificatie Queue ({pendingCards.length})
            </button>
            <button
              onClick={() => { setActiveTab('pendingSales'); loadPendingSales(); }}
              className={`pb-3 px-1 font-semibold text-sm transition border-b-2 ${
                activeTab === 'pendingSales'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Pending Sales ({pendingSales.length})
            </button>
            <button
              onClick={() => { setActiveTab('merchants'); if (merchants.length === 0) loadMerchants(); }}
              className={`pb-3 px-1 font-semibold text-sm transition border-b-2 ${
                activeTab === 'merchants'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Verkopers
            </button>
            <button
              onClick={() => { setActiveTab('escrow'); loadEscrowQueue(); }}
              className={`pb-3 px-1 font-semibold text-sm transition border-b-2 ${
                activeTab === 'escrow'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Escrow Queue ({escrowQueue.length})
            </button>
            <button
              onClick={() => { setActiveTab('stats'); if (statsCards.length === 0 && statsTransactions.length === 0) loadStats(); }}
              className={`pb-3 px-1 font-semibold text-sm transition border-b-2 ${
                activeTab === 'stats'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Stats
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Queue Tab */}
        {activeTab === 'queue' && (
          <>
            {pendingCards.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <div className="text-6xl mb-4">✅</div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Alles verwerkt!</h2>
                <p className="text-gray-600">Geen cadeaubonnen in afwachting van verificatie.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingCards.map((card) => (
                  <div key={card.id} className="bg-white rounded-lg shadow hover:shadow-md transition">
                    <div className="p-6">
                      <div className="flex items-start justify-between">
                        {/* Left: Card Info */}
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-3">
                            <h3 className="text-xl font-bold text-gray-900">{card.brand}</h3>
                            <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-semibold">
                              Pending review
                            </span>
                            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                              getSellerMaturity(card.sellerId, card.completedSalesCount) === 'Nieuw'
                                ? 'bg-yellow-100 text-yellow-700'
                                : 'bg-green-100 text-green-700'
                            }`}>
                              {getSellerMaturity(card.sellerId, card.completedSalesCount)}
                            </span>
                            <span className="text-sm text-gray-500">{getTimeAgo(card.createdAt)}</span>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 mb-4">
                            <div>
                              <p className="text-sm text-gray-600">Waarde</p>
                              <p className="text-lg font-bold text-gray-900">{formatPrice(card.cardValue)}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">Verkoper ontvangt</p>
                              <p className="text-lg font-bold text-green-600">{formatPrice(card.sellerPayout)}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">Verkoper</p>
                              <p className="text-sm font-medium text-gray-900">{card.sellerEmail}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">Ingediend</p>
                              <p className="text-sm text-gray-900">{formatDate(card.createdAt)}</p>
                            </div>
                          </div>

                          {/* Card Details */}
                          <div className="bg-gray-50 rounded p-3 mb-4">
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-xs text-gray-600 mb-1">Cadeaubonnummer</p>
                              {getBalanceCheckUrl(card.brand) && (
                                <a 
                                  href={getBalanceCheckUrl(card.brand)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm text-blue-600 hover:underline flex items-center"
                                >
                                  🔍 Check Saldo →
                                </a>
                              )}
                            </div>
                            <p className="font-mono text-sm text-gray-900">{card.cardNumber}</p>
                            {card.pinCode && (
                              <>
                                <p className="text-xs text-gray-600 mt-2 mb-1">PIN</p>
                                <p className="font-mono text-sm text-gray-900">{card.pinCode}</p>
                              </>
                            )}
                          </div>

                          {/* Compliance Status */}
                          <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
                            <p className="text-xs text-blue-700 font-semibold mb-1">OPP Compliance Status</p>
                            <div className="flex items-center space-x-2">
                              <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                card.sellerCompliance?.status === 'verified' 
                                  ? 'bg-green-100 text-green-700'
                                  : card.sellerCompliance?.status === 'pending'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : 'bg-red-100 text-red-700'
                              }`}>
                                {card.sellerCompliance?.status || 'Unknown'}
                              </span>
                              <span className="text-xs text-gray-600">
                                Level {card.sellerCompliance?.level || '?'}
                              </span>
                            </div>
                          </div>

                          {/* Card Photo */}
                          {card.imageUrl && (
                            <div className="mb-4">
                              <p className="text-sm text-gray-600 mb-2">Foto</p>
                              <img 
                                src={card.imageUrl} 
                                alt="Card" 
                                className="rounded-lg border-2 border-gray-200 max-w-md cursor-pointer hover:border-red-500 transition"
                                onClick={() => window.open(card.imageUrl, '_blank')}
                              />
                            </div>
                          )}
                        </div>

                        {/* Right: Actions */}
                        <div className="ml-6 flex flex-col space-y-3 lg:w-64">
                          <div>
                            <label className="block text-sm font-semibold text-gray-900 mb-2">
                              Geverifieerd saldo
                            </label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={verifiedBalances[card.id] || ''}
                              onChange={(e) => setVerifiedBalances((current) => ({
                                ...current,
                                [card.id]: e.target.value,
                              }))}
                              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none"
                              placeholder="Bijv. 50.00"
                            />
                          </div>
                          <button
                            onClick={() => handleApprove(card)}
                            disabled={actionLoading}
                            className="px-6 py-3 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            ✓ Goedkeuren
                          </button>
                          <button
                            onClick={() => openRejectModal(card)}
                            disabled={actionLoading}
                            className="px-6 py-3 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            ✗ Afwijzen
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'pendingSales' && (
          <>
            {pendingSalesLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto mb-4"></div>
                <p className="text-gray-600">Pending sales laden...</p>
              </div>
            ) : pendingSales.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <div className="text-6xl mb-4">🧾</div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Geen sales in afwachting</h2>
                <p className="text-gray-600">Nieuwe verkoper-sales die een tweede check nodig hebben verschijnen hier.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingSales.map((sale) => (
                  <div key={sale.id} className="bg-white rounded-lg shadow p-6">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-4">
                          <h3 className="text-xl font-bold text-gray-900">{sale.brand}</h3>
                          <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-semibold">
                            Pending admin approval
                          </span>
                          <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-semibold">
                            {getSellerMaturity(sale.sellerId, sale.completedSalesCount)}
                          </span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
                          <div>
                            <p className="text-sm text-gray-600">Kaart ID</p>
                            <p className="text-sm font-mono text-gray-900 break-all">{sale.id}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Waarde</p>
                            <p className="text-sm font-semibold text-gray-900">{formatPrice(sale.originalValue || 0)}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Koper</p>
                            <p className="text-sm text-gray-900">{sale.buyerEmail}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Verkoper</p>
                            <p className="text-sm text-gray-900">{sale.sellerEmail}</p>
                          </div>
                        </div>

                        <div className="bg-gray-50 rounded p-3 mb-4">
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-xs text-gray-600 mb-1">Cadeaubonnummer</p>
                            {getBalanceCheckUrl(sale.brand) && (
                              <a
                                href={getBalanceCheckUrl(sale.brand)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 hover:underline flex items-center"
                              >
                                🔍 Check Saldo →
                              </a>
                            )}
                          </div>
                          <p className="font-mono text-sm text-gray-900">{sale.cardNumber}</p>
                          {sale.pinCode && (
                            <>
                              <p className="text-xs text-gray-600 mt-2 mb-1">PIN</p>
                              <p className="font-mono text-sm text-gray-900">{sale.pinCode}</p>
                            </>
                          )}
                        </div>
                      </div>

                      <div className="lg:w-64 space-y-3">
                        <div>
                          <label className="block text-sm font-semibold text-gray-900 mb-2">
                            Geverifieerd saldo
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={verifiedBalances[sale.id] || ''}
                            onChange={(e) => setVerifiedBalances((current) => ({
                              ...current,
                              [sale.id]: e.target.value,
                            }))}
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none"
                            placeholder="Bijv. 50.00"
                          />
                        </div>
                        <button
                          onClick={() => handleApproveSale(sale)}
                          disabled={actionLoading}
                          className="w-full px-6 py-3 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          ✓ Goedkeuren
                        </button>
                        <button
                          onClick={() => openSaleRejectModal(sale)}
                          disabled={actionLoading}
                          className="w-full px-6 py-3 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          ✗ Afwijzen
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'escrow' && (
          <>
            {escrowQueueLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto mb-4"></div>
                <p className="text-gray-600">Escrow transacties laden...</p>
              </div>
            ) : escrowQueue.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <div className="text-6xl mb-4">💸</div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Geen openstaande escrow releases</h2>
                <p className="text-gray-600">Alle verkochte kaarten zijn al verwerkt of nog niet verkocht.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {escrowQueue.map((card) => {
                  const eligible = isEscrowEligible(card.escrowEligibleAt);
                  const disputeBlocksCompletion =
                    card.disputeStatus === 'opened' || card.disputeStatus === 'resolvedForBuyer';
                  const investigationActive = card.disputeStatus && card.disputeStatus !== 'none';
                  const canExtendEscrow = Boolean(card.oppCanExtendEscrow && investigationActive);

                  return (
                    <div key={card.id} className="bg-white rounded-lg shadow p-6">
                      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-4">
                            <h3 className="text-xl font-bold text-gray-900">{card.brand}</h3>
                            <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
                              Verkocht
                            </span>
                            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                              disputeBlocksCompletion
                                ? 'bg-red-100 text-red-700'
                                : eligible
                                ? 'bg-green-100 text-green-700'
                                : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {disputeBlocksCompletion ? 'Dispuut blokkeert' : eligible ? 'Vrij te geven' : 'Wachttijd actief'}
                            </span>
                            {card.oppStatus && (
                              <span className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm font-semibold">
                                OPP: {card.oppStatus}
                              </span>
                            )}
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                            <div>
                              <p className="text-sm text-gray-600">Kaart ID</p>
                              <p className="text-sm font-mono text-gray-900 break-all">{card.id}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">Verkoper</p>
                              <p className="text-sm font-semibold text-gray-900">{card.sellerName}</p>
                              <p className="text-sm text-gray-500">{card.sellerEmail}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">Verkocht op</p>
                              <p className="text-sm text-gray-900">{formatDate(card.createdAt || card.soldAt)}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">Vrijgeven vanaf</p>
                              <p className="text-sm text-gray-900">{formatDate(card.escrowEligibleAt)}</p>
                              <p className={`text-xs mt-1 ${eligible ? 'text-green-600' : 'text-yellow-700'}`}>
                                {getTimeUntil(card.escrowEligibleAt)}
                              </p>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                            <div className="bg-gray-50 rounded-lg p-3">
                              <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Dispute status</p>
                              <p className="font-semibold text-gray-900">{card.disputeStatus || 'none'}</p>
                            </div>
                            <div className="bg-gray-50 rounded-lg p-3">
                              <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Bedrag</p>
                              <p className="font-semibold text-gray-900">{formatPrice(card.amount || card.sellingPrice || 0)}</p>
                            </div>
                            <div className="bg-gray-50 rounded-lg p-3">
                              <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Voltooide sales verkoper</p>
                              <p className="font-semibold text-gray-900">{card.completedSalesCount || 0}</p>
                            </div>
                          </div>
                        </div>

                        <div className="lg:w-56 space-y-3">
                          <button
                            onClick={() => handleMarkTransactionCompleted(card)}
                            disabled={actionLoading}
                            className="w-full px-5 py-3 bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Sync OPP Status
                          </button>
                          <button
                            onClick={() => handleExtendEscrow(card)}
                            disabled={actionLoading || !canExtendEscrow}
                            title={
                              card.oppStatus === 'completed'
                                ? 'OPP heeft deze transactie al afgerond; verlengen kan niet meer.'
                                : !investigationActive
                                ? 'Alleen gebruiken bij een dispuut of actieve admin-investigatie.'
                                : !card.oppCanExtendEscrow
                                ? 'OPP staat verlengen alleen toe bij created, pending of reserved.'
                                : 'Verleng escrow met 48 uur.'
                            }
                            className="w-full px-5 py-3 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Verleng escrow (+48u)
                          </button>
                          <p className="text-xs text-gray-500 mt-3">
                            Sync leest alleen OPP en werkt Swapify bij. Handmatig verlengen kan alleen zolang OPP de escrow nog open heeft.
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {activeTab === 'stats' && (
          <div className="space-y-8">
            <div className="rounded-[2rem] border border-orange-100 bg-gradient-to-br from-orange-50 via-white to-rose-50 p-6 shadow-[0_24px_80px_-48px_rgba(234,88,12,0.4)]">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.24em] text-red-500">Stats</p>
                  <h2 className="mt-2 text-3xl font-bold text-slate-900">Overzicht van listings en transacties</h2>
                  <p className="mt-2 max-w-2xl text-sm text-slate-600">
                    Alle cijfers op basis van de gekozen periode. Gebruik verversen om de laatste Firestore-data opnieuw op te halen.
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  {STATS_PERIODS.map((period) => (
                    <button
                      key={period.id}
                      onClick={() => setStatsPeriod(period.id)}
                      className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                        statsPeriod === period.id
                          ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg shadow-orange-500/25'
                          : 'bg-white text-slate-600 ring-1 ring-orange-100 hover:text-slate-900'
                      }`}
                    >
                      {period.label}
                    </button>
                  ))}
                  <button
                    onClick={loadStats}
                    disabled={statsLoading}
                    className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {statsLoading ? 'Bezig...' : '↻ Ververs'}
                  </button>
                </div>
              </div>
            </div>

            {statsLoading ? (
              <div className="rounded-[2rem] border border-orange-100 bg-white p-12 text-center shadow-[0_20px_60px_-40px_rgba(234,88,12,0.35)]">
                <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-red-500"></div>
                <p className="text-slate-600">Statistieken laden...</p>
              </div>
            ) : (
              <>
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  {statsMetricCards.map((metric) => (
                    <StatsMetricCard
                      key={metric.label}
                      label={metric.label}
                      value={metric.value}
                      helper={metric.helper}
                    />
                  ))}
                </div>

                <div className="grid gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(340px,1fr)]">
                  <div className="overflow-hidden rounded-[2rem] border border-orange-100 bg-white shadow-[0_20px_60px_-40px_rgba(234,88,12,0.35)]">
                    <div className="border-b border-orange-100 px-6 py-5">
                      <h3 className="text-xl font-bold text-slate-900">Transactie-overzicht</h3>
                      <p className="mt-1 text-sm text-slate-500">
                        {filteredStatsTransactions.length === 0
                          ? 'Geen transacties in deze periode.'
                          : `${formatCount(filteredStatsTransactions.length)} transacties, nieuwste eerst.`}
                      </p>
                    </div>

                    {filteredStatsTransactions.length === 0 ? (
                      <div className="px-6 py-12 text-center">
                        <div className="mb-4 text-5xl">🧾</div>
                        <h4 className="text-xl font-bold text-slate-900">Nog geen transacties gevonden</h4>
                        <p className="mt-2 text-sm text-slate-500">
                          Kies een andere periode of ververs de data als je net nieuwe verkopen verwacht.
                        </p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[840px]">
                          <thead>
                            <tr className="bg-orange-50/70 text-left">
                              <th className="px-6 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Merk</th>
                              <th className="px-6 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Waarde</th>
                              <th className="px-6 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Verkoopprijs</th>
                              <th className="px-6 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Platformkosten</th>
                              <th className="px-6 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Koper</th>
                              <th className="px-6 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Verkoper</th>
                              <th className="px-6 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Status</th>
                              <th className="px-6 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Refund</th>
                              <th className="px-6 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Datum</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-orange-100">
                            {filteredStatsTransactions.map((transaction) => {
                              const isSelected = selectedStatsTransaction?.id === transaction.id;

                              return (
                                <tr
                                  key={transaction.id}
                                  onClick={() => setSelectedStatsTransactionId(transaction.id)}
                                  className={`cursor-pointer transition ${
                                    isSelected ? 'bg-orange-50/80' : 'hover:bg-orange-50/40'
                                  }`}
                                >
                                  <td className="px-6 py-4">
                                    <p className="font-semibold text-slate-900">{transaction.brand}</p>
                                    <p className="text-xs text-slate-500">#{transaction.cardId}</p>
                                  </td>
                                  <td className="px-6 py-4 text-sm text-slate-700">{formatPrice(transaction.originalValue)}</td>
                                  <td className="px-6 py-4 text-sm font-semibold text-slate-900">{formatPrice(transaction.sellingPrice)}</td>
                                  <td className="px-6 py-4 text-sm text-slate-700">{formatPrice(transaction.platformFee)}</td>
                                  <td className="px-6 py-4 text-sm text-slate-700">{transaction.buyerEmail}</td>
                                  <td className="px-6 py-4 text-sm text-slate-700">{transaction.sellerEmail}</td>
                                  <td className="px-6 py-4">
                                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
                                      transaction.status === 'completed'
                                        ? 'bg-emerald-100 text-emerald-700'
                                        : transaction.status === 'rejected'
                                        ? 'bg-red-100 text-red-700'
                                        : 'bg-amber-100 text-amber-700'
                                    }`}>
                                      {transaction.status}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4">
                                    <RefundStatusBadge status={transaction.refundStatus} />
                                  </td>
                                  <td className="px-6 py-4 text-sm text-slate-700">{formatDate(transaction.createdAt)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  <div className="rounded-[2rem] border border-orange-100 bg-white p-6 shadow-[0_20px_60px_-40px_rgba(234,88,12,0.35)]">
                    <div className="flex items-start justify-between gap-4 border-b border-orange-100 pb-4">
                      <div>
                        <p className="text-sm font-semibold uppercase tracking-[0.22em] text-red-500">Details</p>
                        <h3 className="mt-2 text-xl font-bold text-slate-900">
                          {selectedStatsTransaction ? selectedStatsTransaction.brand : 'Selecteer een transactie'}
                        </h3>
                      </div>
                      {selectedStatsTransaction ? (
                        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
                          getSellerMaturity(
                            selectedStatsTransaction.sellerId,
                            selectedStatsTransaction.completedSalesCount
                          ) === 'Nieuw'
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-emerald-100 text-emerald-700'
                        }`}>
                          {getSellerMaturity(
                            selectedStatsTransaction.sellerId,
                            selectedStatsTransaction.completedSalesCount
                          )}
                        </span>
                      ) : null}
                    </div>

                    {!selectedStatsTransaction ? (
                      <div className="py-12 text-center">
                        <div className="mb-4 text-5xl">👈</div>
                        <p className="text-sm text-slate-500">Klik op een transactie om de details hier te bekijken.</p>
                      </div>
                    ) : (
                      <div className="mt-6 grid gap-4">
                        {[
                          ['Card ID', selectedStatsTransaction.cardId],
                          ['Merk', selectedStatsTransaction.brand],
                          ['Kaartwaarde', formatPrice(selectedStatsTransaction.originalValue)],
                          ['Verkoopprijs', formatPrice(selectedStatsTransaction.sellingPrice)],
                          ['Platformkosten', formatPrice(selectedStatsTransaction.platformFee)],
                          ['Uitbetaling verkoper', formatPrice(selectedStatsTransaction.sellerPayout)],
                          ['Koper', selectedStatsTransaction.buyerEmail],
                          ['Verkoper', selectedStatsTransaction.sellerEmail],
                          ['Verkoperstype', getSellerMaturity(
                            selectedStatsTransaction.sellerId,
                            selectedStatsTransaction.completedSalesCount
                          )],
                          ['Transactiestatus', selectedStatsTransaction.status],
                          ['Dispuutstatus', selectedStatsTransaction.disputeStatus || 'none'],
                          ['Payment ID (OPP)', selectedStatsTransaction.paymentId || '-'],
                          ['Aangemaakt op', formatDate(selectedStatsTransaction.createdAt)],
                          ['Voltooid op', formatDate(selectedStatsTransaction.completedAt)],
                          ['Escrow vrij vanaf', formatDate(selectedStatsTransaction.escrowEligibleAt)],
                          ['Escrow vrijgegeven op', formatDate(selectedStatsTransaction.escrowReleasedAt)],
                        ].map(([label, value]) => (
                          <div
                            key={label}
                            className="rounded-2xl border border-orange-100 bg-orange-50/40 px-4 py-3"
                          >
                            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</p>
                            <p className="mt-2 break-words text-sm font-semibold text-slate-900">{value || '-'}</p>
                          </div>
                        ))}
                        <div className="rounded-2xl border border-orange-100 bg-orange-50/40 px-4 py-3">
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Refundstatus</p>
                          <div className="mt-2">
                            <RefundStatusBadge status={selectedStatsTransaction.refundStatus} />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Merchants Tab */}
        {activeTab === 'merchants' && (
          <>
            {merchantsLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto mb-4"></div>
                <p className="text-gray-600">Verkopers laden...</p>
              </div>
            ) : merchants.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <div className="text-6xl mb-4">👥</div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Geen verkopers</h2>
                <p className="text-gray-600">Er zijn nog geen verkopers met een OPP account.</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Verkoper</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Maturity</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Account leeftijd</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Completed sales</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">OPP Status</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Compliance</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Openstaand</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Listings</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Volume</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {merchants.map((m) => {
                        const openReqs = m.opp?.compliance?.requirements?.filter(r => r.status !== 'verified') || [];
                        return (
                          <tr key={m.uid} className="hover:bg-gray-50">
                            <td className="px-6 py-4">
                              <p className="font-semibold text-gray-900">{m.name || 'Geen naam'}</p>
                              <p className="text-sm text-gray-500">{m.email}</p>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                m.isNewSeller ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'
                              }`}>
                                {m.isNewSeller ? 'New seller' : 'Established'}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <p className="font-semibold text-gray-900">
                                {m.accountAgeDays != null ? `${m.accountAgeDays} dagen` : 'Onbekend'}
                              </p>
                              <p className="text-xs text-gray-500">{formatDate(m.authCreatedAt || m.createdAt)}</p>
                            </td>
                            <td className="px-6 py-4">
                              <p className="font-semibold text-gray-900">{m.completedSalesCount ?? 0}</p>
                              <p className="text-xs text-gray-500">Drempel: 5 sales</p>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                m.opp?.status === 'live' ? 'bg-green-100 text-green-700' :
                                m.opp?.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                m.opp?.status === 'new' ? 'bg-blue-100 text-blue-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {m.opp?.status || 'Onbekend'}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                m.opp?.compliance?.status === 'verified' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                              }`}>
                                Level {m.opp?.compliance?.level || '?'} — {m.opp?.compliance?.status || 'onbekend'}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              {openReqs.length > 0 ? (
                                <div className="space-y-1">
                                  {openReqs.map((r, i) => (
                                    <p key={i} className="text-xs text-orange-600">{r.type}</p>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-xs text-green-600">✓ Geen</span>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm">
                                <span className="text-yellow-600">{m.stats.pending}P</span>
                                {' / '}
                                <span className="text-green-600">{m.stats.active}A</span>
                                {' / '}
                                <span className="text-blue-600">{m.stats.sold}S</span>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <p className="font-semibold text-gray-900">{formatPrice(m.stats.totalVolume)}</p>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
        
      {showSaleRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Sale afwijzen</h3>
            <p className="text-sm text-gray-600 mb-4">
              {selectedSale?.brand} - {formatPrice(selectedSale?.originalValue || 0)}
            </p>

            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Reden voor afwijzing
            </label>
            <textarea
              value={saleRejectionReason}
              onChange={(e) => setSaleRejectionReason(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none mb-4 min-h-28"
              placeholder="Omschrijf waarom deze sale is afgewezen"
            />

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowSaleRejectModal(false);
                  setSelectedSale(null);
                  setSaleRejectionReason('');
                }}
                disabled={actionLoading}
                className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg font-semibold hover:border-gray-400 transition disabled:opacity-50"
              >
                Annuleren
              </button>
              <button
                onClick={handleRejectSale}
                disabled={actionLoading || !saleRejectionReason}
                className="flex-1 px-4 py-3 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading ? 'Bezig...' : 'Afwijzen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Cadeaubon afwijzen</h3>
            <p className="text-sm text-gray-600 mb-4">
              {selectedCard?.brand} - {formatPrice(selectedCard?.cardValue)}
            </p>
            
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Reden voor afwijzing
            </label>
            <select
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none mb-4"
            >
              <option value="">Selecteer een reden...</option>
              {REJECTION_REASONS.map((reason) => (
                <option key={reason} value={reason}>{reason}</option>
              ))}
            </select>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setSelectedCard(null);
                  setRejectionReason('');
                }}
                disabled={actionLoading}
                className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg font-semibold hover:border-gray-400 transition disabled:opacity-50"
              >
                Annuleren
              </button>
              <button
                onClick={handleReject}
                disabled={actionLoading || !rejectionReason}
                className="flex-1 px-4 py-3 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading ? 'Bezig...' : 'Afwijzen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};



export default AdminDashboard;
